import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Delete as DeleteIcon } from '@mui/icons-material';

const LinkText = styled('span')`
  color: #1976d2;
  text-decoration: underline;
`;

const moveItem = (arr, fromIndex, toIndex) => {
  const copy = arr.slice();
  const [moved] = copy.splice(fromIndex, 1);
  copy.splice(toIndex, 0, moved);
  return copy;
};

const getBackgroundColor = (isDragging, isOver) => {
  if (isDragging) return 'action.selected';
  if (isOver) return 'action.hover';
  return 'transparent';
};

const buildPositions = (items, page, pageSize) =>
  items.map((it, idx) => ({
    id: String(it.id),
    orderNumber: (page - 1) * pageSize + idx + 1,
  }));

const diffPositions = (prevItems, nextItems, page, pageSize) => {
  const before = buildPositions(prevItems, page, pageSize);
  const after  = buildPositions(nextItems, page, pageSize);
  const prevMap = new Map(before.map((x) => [x.id, x.orderNumber]));
  return after.filter((x) => prevMap.get(x.id) !== x.orderNumber);
};

const SortableList = ({
  items,
  page,
  pageSize,
  onRowClick,
  onBulkReorder,
  sendOnlyChanges = true,
  onDelete,
}) => {
  const [localItems, setLocalItems] = useState(items);
  const [draggingId, setDraggingId] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const snapshotRef = useRef(items);
  const finalizingRef = useRef(false);

  useEffect(() => {
    setLocalItems(items);
    snapshotRef.current = items;
  }, [items]);

  const finalizeDrag = useCallback(() => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;

    const id = draggingId;
    const dropIndex = overIndex;

    setDraggingId(null);
    setOverIndex(null);

    if (id == null || dropIndex == null) {
      finalizingRef.current = false;
      return;
    }

    setLocalItems((prev) => {
      const map = new Map(prev.map((it, idx) => [String(it.id), idx]));
      const fromIndex = map.get(id);

      if (fromIndex == null || fromIndex === dropIndex) {
        finalizingRef.current = false;
        return prev;
      }

      const next = moveItem(prev, fromIndex, dropIndex);

      const payload = sendOnlyChanges
        ? diffPositions(prev, next, page, pageSize)
        : buildPositions(next, page, pageSize);

      Promise.resolve(onBulkReorder(payload))
        .catch(() => {
          // setLocalItems(snapshotRef.current);
        })
        .finally(() => {
          snapshotRef.current = next;
          finalizingRef.current = false;
        });

      return next;
    });
  }, [draggingId, overIndex, page, pageSize, onBulkReorder, sendOnlyChanges]);

  const onRowDragStart = (e, id) => {
    const strId = String(id);
    setDraggingId(strId);
    e.dataTransfer.setData('text/plain', strId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onRowDragEnter = (_e, index) => setOverIndex(index);

  const onRowDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(index);
  };

  return (
    <Paper variant="outlined" sx={{ width: '100%', userSelect: 'none' }}>
      <List
        disablePadding
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (
            localItems.length > 0 &&
            overIndex == null &&
            draggingId != null
          ) {
            setOverIndex(localItems.length - 1);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          finalizeDrag();
        }}
      >
        {localItems.map((item, index) => {
          const isDragging = draggingId === String(item.id);
          const isOver = overIndex === index;

          return (
            <ListItem
              key={item.id}
              draggable
              divider
              onDragStart={(e) => onRowDragStart(e, item.id)}
              onDragEnter={(e) => onRowDragEnter(e, index)}
              onDragOver={(e) => onRowDragOver(e, index)}
              onDrop={(e) => {
                e.preventDefault();
                finalizeDrag();
              }}
              onDragEnd={finalizeDrag}
              sx={{
                cursor: 'grab',
                bgcolor: getBackgroundColor(isDragging, isOver),
                transition: 'background-color 120ms ease',
              }}
              onClick={() => onRowClick?.(item.id)}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: '100%' }}
              >
                <IconButton
                  edge="start"
                  aria-label="drag"
                  title="Drag to reorder"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ cursor: 'grab' }}
                >
                  <DragIndicatorIcon />
                </IconButton>

                <ListItemText
                  primary={item.title}
                  secondary={
                    <>
                      {item.description && <span>{item.description} • </span>}
                      <LinkText>{item.link}</LinkText>
                    </>
                  }
                />
                <IconButton
                  edge="end"
                  aria-label="delete"
                  title="Delete resource"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(item.id);
                  }}
                >
                  <DeleteIcon color="primary" />
                </IconButton>
              </Stack>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};

SortableList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      link: PropTypes.string.isRequired,
    }),
  ).isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onRowClick: PropTypes.func,
  onBulkReorder: PropTypes.func.isRequired,
  sendOnlyChanges: PropTypes.bool,
  onDelete: PropTypes.func,
};

export default SortableList;
